import React, { useState, useEffect, useRef } from "react";

// =============================================================================
// SUB COMPONENT: Team Betting Game (Final Fixed Version)
// =============================================================================
const AVATARS = ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯"];

// 돌발 이벤트 목록 (확률 조정됨)
const EVENTS = [
  { type: "BOOST", text: "🔥 부스터 ON!", value: 12, icon: "🔥" },
  { type: "SLIP", text: "🍌 꽈당!", value: -4, icon: "💫" },
  { type: "KIDNAP", text: "🛸 납치당함", value: -12, icon: "👽" },
  { type: "WIND", text: "🌪️ 태풍이 붊", value: 8, icon: "🌪️" },
  { type: "NAP", text: "💤 딴짓 중...", value: 0, icon: "💤" }, // 멈춤
];

function TeamBettingGame() {
  // 상태 관리
  const [participants, setParticipants] = useState([
    "채연",
    "민경",
    "남영",
    "수연",
    "기욱",
    "현승",
  ]);
  const [inputValue, setInputValue] = useState("");

  // 내기 규칙
  const [gameMode, setGameMode] = useState("win");
  const [penaltyContent, setPenaltyContent] = useState("커피 쏘기");

  // 게임 상태
  const [gameState, setGameState] = useState("idle");
  const [progress, setProgress] = useState({});
  const [winner, setWinner] = useState(null);
  const [commentary, setCommentary] = useState("🏁 레이싱 대기 중...");
  const [statuses, setStatuses] = useState({}); // 현재 상태 아이콘

  const requestRef = useRef();

  // 게임 초기화
  const initGame = () => {
    const initialProgress = {};
    const initialStatuses = {};
    participants.forEach((p) => {
      initialProgress[p] = 0;
      initialStatuses[p] = "";
    });
    setProgress(initialProgress);
    setStatuses(initialStatuses);
    setGameState("idle");
    setWinner(null);
    setCommentary("🏁 선수 입장 완료! 출발 준비...");
  };

  // 참가자 추가/삭제
  const addParticipant = () => {
    if (inputValue.trim() && !participants.includes(inputValue)) {
      setParticipants([...participants, inputValue]);
      setInputValue("");
    }
  };
  const removeParticipant = (name) => {
    setParticipants(participants.filter((p) => p !== name));
  };

  // 게임 시작
  const startGame = () => {
    if (participants.length < 2) {
      alert("최소 2명 이상 참여해야 합니다!");
      return;
    }
    initGame();
    setTimeout(() => {
      setGameState("playing");
      setCommentary("🔫 땅! 출발했습니다!");
    }, 800);
  };

  // 🔄 애니메이션 루프 (속도 및 움직임 로직 개선)
  const updateGame = () => {
    if (gameState !== "playing") return;

    setProgress((prevProgress) => {
      const newProgress = { ...prevProgress };
      const newStatuses = { ...statuses };
      let isFinished = false;
      let currentWinner = null;
      let eventText = null;

      participants.forEach((p) => {
        if (newProgress[p] >= 100) return; // 이미 도착했으면 패스

        // 1. 이벤트 발생 확률 (0.8% - 매우 낮음, 가끔 발생)
        const isEvent = Math.random() < 0.008;

        if (isEvent) {
          const randomEvent = EVENTS[Math.floor(Math.random() * EVENTS.length)];

          // 값 적용 (0~100 사이 유지)
          let nextVal = newProgress[p] + randomEvent.value;
          if (nextVal < 0) nextVal = 0;
          if (nextVal > 100) nextVal = 100;

          newProgress[p] = nextVal;
          newStatuses[p] = randomEvent.icon;

          // 중계 멘트 업데이트 (우선순위 높음)
          if (randomEvent.type !== "NAP") {
            // 잠자는 건 중계 안 함
            eventText = `${p}: ${randomEvent.text}`;
          }
        } else {
          // 2. 평소 주행 (속도: 0.05 ~ 0.15) -> 아주 천천히 움직임
          // 1프레임당 이만큼 이동하므로 60프레임(1초)에 약 3~9% 이동
          const speed = Math.random() * 0.15 + 0.02;
          newProgress[p] = Math.min(newProgress[p] + speed, 100);

          // 상태 아이콘 지우기 (약간의 확률로)
          if (Math.random() < 0.02) newStatuses[p] = "";
        }

        // 3. 완주 체크
        if (newProgress[p] >= 100 && !isFinished) {
          isFinished = true;
          currentWinner = p;
        }
      });

      if (eventText) {
        setCommentary(eventText);
      }
      if (isFinished) {
        setWinner(currentWinner);
        setGameState("finished");
        setCommentary(`🎉 경기 종료! 우승자: ${currentWinner}`);
      }

      setStatuses(newStatuses); // 상태 아이콘 업데이트
      return newProgress;
    });
  };

  useEffect(() => {
    if (gameState === "playing") {
      const animate = () => {
        updateGame();
        requestRef.current = requestAnimationFrame(animate);
      };
      requestRef.current = requestAnimationFrame(animate);
    } else {
      cancelAnimationFrame(requestRef.current);
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, participants]); // statuses 제외 (렌더링 최적화)

  return (
    <div className="border rounded-xl p-8 bg-white shadow-sm mt-8">
      {/* Header */}
      <div className="mb-6 border-b pb-4 flex justify-between items-end">
        <div>
          <h2 className="text-title text-2xl">⚡ PMIS 혼돈의 레이싱</h2>
          <p className="text-body text-gray-500 mt-1">
            누가{" "}
            <span className="text-highlight font-bold">{penaltyContent}</span>{" "}
            당첨될까요?
          </p>
        </div>
        <div className="text-sm text-gray-400">Ver 3.0 (Fixed)</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* [왼쪽] 설정 패널 */}
        <div className="col-span-1 space-y-6 border-r pr-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <label className="text-subtitle block mb-3">📋 규칙 설정</label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  checked={gameMode === "win"}
                  onChange={() => setGameMode("win")}
                  className="accent-blue-500"
                />
                <span className="text-sm font-medium text-blue-600">
                  🎉 1등이 혜택
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  checked={gameMode === "lose"}
                  onChange={() => setGameMode("lose")}
                  className="accent-red-500"
                />
                <span className="text-sm font-medium text-red-600">
                  💣 1등이 독박
                </span>
              </label>
            </div>
            <input
              type="text"
              className="w-full border p-2 rounded text-sm"
              placeholder="벌칙 내용"
              value={penaltyContent}
              onChange={(e) => setPenaltyContent(e.target.value)}
              disabled={gameState === "playing"}
            />
          </div>

          <div>
            <label className="text-subtitle block mb-2">
              🏃 참가자 ({participants.length}명)
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                className="flex-1 border p-2 rounded"
                placeholder="이름"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addParticipant()}
                disabled={gameState === "playing"}
              />
              <button
                onClick={addParticipant}
                className="btn-create-sm whitespace-nowrap"
                disabled={gameState === "playing"}
              >
                추가
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {participants.map((name, idx) => (
                <div
                  key={idx}
                  className="bg-white border px-3 py-1 rounded-full flex items-center gap-2 text-sm shadow-sm"
                >
                  <span>
                    {AVATARS[idx % AVATARS.length]} {name}
                  </span>
                  {gameState !== "playing" && (
                    <button
                      onClick={() => removeParticipant(name)}
                      className="text-gray-400 hover:text-red-500 font-bold ml-1"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2">
            {gameState === "playing" ? (
              <button className="btn-basic w-full cursor-not-allowed opacity-50 font-bold">
                🔥 경기 진행 중...
              </button>
            ) : gameState === "finished" ? (
              <button
                onClick={startGame}
                className="btn-create w-full py-3 text-lg"
              >
                🔄 다시 하기
              </button>
            ) : (
              <button
                onClick={startGame}
                className="btn-save w-full py-3 text-lg"
              >
                🚀 레이싱 시작
              </button>
            )}
          </div>
        </div>

        {/* [오른쪽] 게임 화면 */}
        <div className="col-span-1 lg:col-span-2 bg-gray-100/50 border rounded-lg p-6 relative min-h-[500px] flex flex-col justify-center overflow-hidden">
          {/* 📢 실시간 중계 박스 */}
          <div className="absolute top-4 left-4 right-4 bg-gray-800 text-white p-3 rounded-lg shadow-lg z-10 text-center opacity-90 transition-all">
            <span className="text-yellow-400 mr-2">📢 LIVE:</span>
            <span className="font-bold">{commentary}</span>
          </div>

          {/* 트랙 영역 */}
          <div className="space-y-8 relative z-0 w-full mt-16 px-2">
            {participants.map((name, idx) => (
              <div key={name} className="relative">
                {/* 이름 및 퍼센트 */}
                <div className="flex justify-between text-sm mb-1 font-bold text-gray-600">
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-gray-200 text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    {name}
                  </span>
                  <span>{Math.floor(progress[name] || 0)}%</span>
                </div>

                {/* 트랙 배경 (회색 바) */}
                <div className="h-4 bg-gray-300 rounded-full relative w-full mt-2">
                  {/* 진행 바 (색깔 채워짐) */}
                  <div
                    className={`h-full rounded-full transition-none ${
                      winner === name ? "bg-yellow-400" : "bg-blue-400"
                    }`}
                    style={{ width: `${progress[name]}%` }}
                  ></div>

                  {/* 🚀 움직이는 캐릭터 (절대 좌표 사용으로 수정됨) */}
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 transition-none z-10 flex flex-col items-center"
                    style={{
                      left: `${progress[name]}%`,
                      marginLeft: "-15px", // 아이콘 중앙 정렬 보정
                    }}
                  >
                    {/* 상태 아이콘 (말풍선) */}
                    {statuses[name] && (
                      <div className="absolute -top-8 text-lg animate-bounce whitespace-nowrap bg-white/80 px-1 rounded border shadow-sm">
                        {statuses[name]}
                      </div>
                    )}
                    {/* 동물 아바타 */}
                    <div className="text-3xl filter drop-shadow-md">
                      {winner === name ? "🚩" : AVATARS[idx % AVATARS.length]}
                    </div>
                  </div>

                  {/* 피니시 라인 */}
                  <div className="absolute top-0 right-0 h-8 -mt-2 w-1 bg-red-500/50 border-r border-dashed border-white"></div>
                </div>
              </div>
            ))}
          </div>

          {/* 결과 모달 */}
          {gameState === "finished" && winner && (
            <div className="absolute inset-0 z-20 bg-black/70 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center text-white animate-[fadeIn_0.3s_ease-out]">
              <div className="text-6xl mb-4 animate-bounce">
                {gameMode === "win" ? "🎊" : "🚨"}
              </div>
              <h3 className="text-4xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-500">
                {winner}
              </h3>
              <p className="text-2xl font-bold mb-6 text-center leading-relaxed">
                {gameMode === "win"
                  ? "1등 당첨! 축하합니다!"
                  : "1등 독박! 벌칙 당첨!"}{" "}
                <br />
                <span className="text-lg font-normal text-gray-300">
                  ({penaltyContent})
                </span>
              </p>
              <button
                onClick={initGame}
                className="px-6 py-2 bg-white text-gray-900 rounded-full font-bold hover:bg-gray-200 transition-colors"
              >
                확인 / 닫기
              </button>
            </div>
          )}

          {/* 대기 화면 */}
          {gameState === "idle" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
              <span className="text-6xl font-black text-gray-500 -rotate-12 select-none">
                READY?
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT: StyleGuide
// =============================================================================
export default function StyleGuide() {
  return (
    <div className="p-8 space-y-12">
      {/* Title */}
      <h1 className="text-2xl font-bold mb-6">PMIS UI Style Guide</h1>

      {/* ============================
          BUTTONS (버튼 스타일)
      ============================ */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Buttons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded-lg p-4">
            <button className="btn-create">등록</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-create</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-edit">수정</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-edit</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-save">저장</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-save</code>
            </p>
          </div>
          {/* 소형 버튼 */}
          <div className="border rounded-lg p-4">
            <button className="btn-create-sm">등록(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-create-sm</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-edit-sm">수정(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-edit-sm</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-save-sm">저장(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-save-sm</code>
            </p>
          </div>
          {/* 삭제 버튼 */}
          <div className="border rounded-lg p-4">
            <button className="btn-delete">삭제</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-delete</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-delete-sm">삭제(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-delete-sm</code>
            </p>
          </div>
          {/* 취소/검색/기본 버튼 */}
          <div className="border rounded-lg p-4">
            <button className="btn-cancel">취소</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-cancel</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-search">검색</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-search</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-basic">기본 버튼</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-basic</code>
            </p>
          </div>
          {/* 소형 취소/기본 */}
          <div className="border rounded-lg p-4">
            <button className="btn-cancel-sm">취소(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-cancel-sm</code>
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <button className="btn-basic-sm">기본(소형)</button>
            <p className="mt-2 text-sm text-gray-600">
              <code>.btn-basic-sm</code>
            </p>
          </div>
        </div>
      </section>

      {/* ============================
          TEXTS(글자 스타일)
      ============================ */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Typography (텍스트)</h2>
        <div className="space-y-5">
          <div>
            <div className="text-title">제목 Title (H1)</div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-title</code>
            </p>
          </div>
          <div>
            <div className="text-subtitle">소제목 Subtitle (H2)</div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-subtitle</code>
            </p>
          </div>
          <div>
            <div className="text-body">
              본문 내용입니다. PMIS 시스템 전반에서 사용되는 기본 텍스트
              스타일입니다.
            </div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-body</code>
            </p>
          </div>
          <div>
            <div className="text-highlight">강조 텍스트 내용입니다.</div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-highlight</code>
            </p>
          </div>
          <div>
            <div className="text-gray-medium">회색 중간 강조 텍스트</div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-gray-medium</code>
            </p>
          </div>
          <div>
            <div className="text-muted">
              설명 / 데이터 없음 표시용 회색 텍스트
            </div>
            <p className="text-sm text-gray-600">
              클래스명: <code>text-muted</code>
            </p>
          </div>
        </div>
      </section>

      {/* ============================
          TABLE STYLES
      ============================ */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Table Styles</h2>
        <table className="border w-full">
          <tbody>
            <tr>
              <td className="tbl-label-bg">Label 셀</td>
              <td className="tbl-value-bg">Value 셀</td>
            </tr>
          </tbody>
        </table>
        <p className="text-sm text-gray-600 mt-3">
          클래스명: <code>tbl-label-bg</code>, <code>tbl-value-bg</code>
        </p>
      </section>

      {/* ============================
          TEAM BETTING GAME (통합됨)
      ============================ */}
      <section>
        <div className="border-t pt-10">
          <TeamBettingGame />
        </div>
      </section>
    </div>
  );
}
